import {
  addDays,
  isLive,
  parseCheckinOccurrenceKey,
  personalDateOf,
  personalDateOfKey,
  recentOccurrenceDates,
  readCheckinRecurrence,
  resolveCheckinLogs,
  type CheckinDayItem,
  type CheckinLogRecord,
  type LiveCheckinItem,
} from '@wiztime/core';
import { dayLogOf } from '../../../lib/checkinOccurrences';
import { createdAtOfUuidV7 } from '../../../lib/uuidTime';

/** An item unticked on this many of its most recent previous occurrence days fades (requirements §5.3). */
export const FADE_AFTER_DAYS = 3;

/** Earlier than any personal day: the floor of an item whose creation time cannot be read. */
const NO_FLOOR_DATE = '0001-01-01';

/** Lookup key of one item on one personal day. */
const doneKey = (itemId: string, date: string): string => `${itemId}|${date}`;

/**
 * Index of the (item, personal day) pairs with a done occurrence, so each item's check is a few
 * set lookups instead of a log scan. A log counts for the personal day of its occurrence under
 * the current day start (data dictionary §3.2), not for the date it was written with; of a legacy
 * and a new log of one occurrence the newer decides. Logs of items not in `items` are ignored.
 */
export function doneIndex(
  logs: readonly CheckinLogRecord[],
  items: readonly CheckinDayItem[],
  dayStartMin: number,
  legacyDayStartMin: number,
): ReadonlySet<string> {
  const resolved = resolveCheckinLogs(items, logs.filter(isLive).map(dayLogOf), legacyDayStartMin);
  return new Set(
    [...resolved].flatMap(([key, log]) => {
      const parsed = parseCheckinOccurrenceKey(key);
      return log.done && parsed !== null
        ? [doneKey(parsed.itemId, personalDateOfKey(parsed.occurrence, dayStartMin))]
        : [];
    }),
  );
}

/**
 * True when `item` should fade into the collapsed section on `date` (requirements §5.3): it has
 * no done occurrence on `date`, and none on its latest FADE_AFTER_DAYS occurrence days before
 * `date`. Days that are not occurrence days, days before the start date and days up to the item's
 * creation day do not count, so fewer than FADE_AFTER_DAYS countable days never fade the item
 * (an item created within the last three days is never faded). Every-N-days items never fade.
 * `logs` are the check-in logs of the lookback window and of `date` (with a day of margin), or
 * their `doneIndex`.
 */
export function isFaded(
  item: LiveCheckinItem,
  date: string,
  logs: readonly CheckinLogRecord[] | ReadonlySet<string>,
  dayStartMin: number,
  legacyDayStartMin: number = dayStartMin,
): boolean {
  const done =
    logs instanceof Set
      ? logs
      : doneIndex(logs as readonly CheckinLogRecord[], [item], dayStartMin, legacyDayStartMin);
  if (done.has(doneKey(item.id, date))) return false;
  const createdAt = createdAtOfUuidV7(item.id);
  // The creation day itself is not countable (it was still in progress when the item appeared);
  // an unreadable creation time counts as an old item.
  const createdFloor = createdAt === null ? NO_FLOOR_DATE : addDays(personalDateOf(createdAt, dayStartMin), 1);
  const startDate = readCheckinRecurrence(item.data).startDate;
  const floor = startDate !== null && startDate > createdFloor ? startDate : createdFloor;
  const recent = recentOccurrenceDates(item.data, date, FADE_AFTER_DAYS, floor);
  return recent.length === FADE_AFTER_DAYS && recent.every((day) => !done.has(doneKey(item.id, day)));
}
